/**
 * Database Configuration using Dexie.js (IndexedDB wrapper)
 * 
 * This provides a complete, production-ready local database for the MARS-X application.
 * Data is stored in the browser's IndexedDB and synced with Supabase in the background.
 * 
 * DATABASE MIGRATION STRATEGY
 * ===========================
 * 
 * Schema Evolution:
 * -----------------
 * The database uses Dexie's versioning system to manage schema changes safely.
 * Each version increment adds new tables or modifies existing ones.
 * 
 * Version History:
 * - v1: Clean start with vesselId standard (November 2025)
 *   - Complete maritime management schema (70+ tables)
 *   - All vessel references use vesselId (industry standard)
 *   - No migration handlers (fresh deployment for new users)
 *   - Legacy MARS 2 users migrate via separate migration scripts
 * 
 * Migration Workflow:
 * -------------------
 * 1. Each version() declaration defines the schema for that version
 * 2. The .upgrade() handler is called ONLY when upgrading from the previous version
 * 3. New tables start empty (no migration needed)
 * 4. For table modifications, add data transformation logic in .upgrade()
 * 
 * Safety Features:
 * ----------------
 * - Dexie automatically handles version transitions
 * - If upgrade fails, database rolls back to previous version
 * - Console logs track upgrade progress
 * - No data loss during version transitions (existing data preserved)
 * 
 * Adding New Schema Versions:
 * ---------------------------
 * When adding a new version (e.g., v8):
 * 
 * 1. Increment version number:
 *    this.version(8).stores({ ... })
 * 
 * 2. Copy ALL table definitions from v7 (Dexie requirement)
 * 
 * 3. Add new tables or modify indexes:
 *    new_table: 'id, field1, field2, ...'
 * 
 * 4. Add upgrade handler:
 *    .upgrade(async (tx) => {
 *      // Data transformation logic here
 *      // Example: Migrate existing data to new format
 *      const oldData = await tx.table('old_table').toArray();
 *      await tx.table('new_table').bulkAdd(oldData.map(transformFn));
 *    })
 * 
 * 5. Test migration thoroughly:
 *    - Create test data in v7
 *    - Upgrade to v8
 *    - Verify all data preserved correctly
 * 
 * Best Practices:
 * ---------------
 * - NEVER remove version declarations (breaks existing users)
 * - ALWAYS increment version for schema changes
 * - ADD tables by including them in new version
 * - MODIFY indexes by redefining table in new version
 * - TEST migrations with real production data (in dev environment)
 * - DOCUMENT schema changes in version comments
 * - PRESERVE existing data (no data loss acceptable for maritime systems)
 * 
 * Rollback Strategy:
 * ------------------
 * If a migration fails in production:
 * 1. Dexie automatically rolls back to previous version
 * 2. Check browser console for migration error logs
 * 3. Fix upgrade logic and increment version again
 * 4. DO NOT reuse failed version numbers
 * 
 * Reference: https://dexie.org/docs/Tutorial/Design#database-versioning
 */

import Dexie, { Table } from 'dexie';
import { createLogger } from '../utils/logger';
import type {
  Ship,
  Certificate,
  Equipment,
  MaintenanceRecord,
  WorkOrder,
  Personnel,
  CrewCertification,
  WorkingHours,
  Training,
  TrainingRecord,
  Inspection,
  Incident,
  Drill,
  RiskAssessment,
  InventoryItem,
  StockTransaction,
  Requisition,
  Document,
  OilRecordEntry,
  GarbageRecordEntry,
  BallastWaterEntry,
  EmissionRecord,
  Position,
  Passage,
  Waypoint,
  PortCall,
  Audit,
  NonConformity,
  ComplianceCheck,
  User,
  Notification,
  KPI,
  PerformanceMetric,
  SyncItem,
  Form,
  FormSubmission,
  CorrectiveAction,
  AuditTrailEntry
} from '../../types/database';
import type {
  LogbookEntry,
  LogbookTemplate,
  WatchPeriod,
  LogbookMachineryReadings,
  LogbookAuditTrail
} from '../../types/logbook';
import type {
  FuelConsumptionEntry,
  MRVMonitoringPlan,
  MRVAnnualReport,
  MRVVoyageReport,
  MRVPortEmissions,
  THETISMRVSubmission
} from '../../types/mrv';
import type {
  HazardousMaterial,
  IHMDocument,
  SafetyDataSheet,
  MaterialSample,
  IHMInspection,
  MaterialRemoval
} from '../../types/ihm';

const logger = createLogger('Database');

/**
 * Main Database Class
 */
export class MARSXDatabase extends Dexie {
  // Tables
  ships!: Table<Ship, string>;
  certificates!: Table<Certificate, string>;
  equipment!: Table<Equipment, string>;
  maintenanceRecords!: Table<MaintenanceRecord, string>;
  workOrders!: Table<WorkOrder, string>;
  personnel!: Table<Personnel, string>;
  crewCertifications!: Table<CrewCertification, string>;
  workingHours!: Table<WorkingHours, string>;
  trainings!: Table<Training, string>;
  trainingRecords!: Table<TrainingRecord, string>;
  inspections!: Table<Inspection, string>;
  incidents!: Table<Incident, string>;
  drills!: Table<Drill, string>;
  riskAssessments!: Table<RiskAssessment, string>;
  inventoryItems!: Table<InventoryItem, string>;
  stockTransactions!: Table<StockTransaction, string>;
  requisitions!: Table<Requisition, string>;
  documents!: Table<Document, string>;
  oilRecords!: Table<OilRecordEntry, string>;
  garbageRecords!: Table<GarbageRecordEntry, string>;
  ballastRecords!: Table<BallastWaterEntry, string>;
  emissionRecords!: Table<EmissionRecord, string>;
  positions!: Table<Position, string>;
  passages!: Table<Passage, string>;
  waypoints!: Table<Waypoint, string>;
  portCalls!: Table<PortCall, string>;
  audits!: Table<Audit, string>;
  nonConformities!: Table<NonConformity, string>;
  complianceChecks!: Table<ComplianceCheck, string>;
  users!: Table<User, string>;
  notifications!: Table<Notification, string>;
  kpis!: Table<KPI, string>;
  performanceMetrics!: Table<PerformanceMetric, string>;
  syncQueue!: Table<SyncItem, string>;
  forms!: Table<Form, string>;
  form_submissions!: Table<FormSubmission, string>;
  corrective_actions!: Table<CorrectiveAction, string>;
  
  // Audit Trail (Immutable, Append-Only)
  auditTrail!: Table<AuditTrailEntry, string>;
  
  // Logbook tables
  logbook_entries!: Table<LogbookEntry, string>;
  logbook_templates!: Table<LogbookTemplate, string>;
  logbook_watch_periods!: Table<WatchPeriod, string>;
  logbook_machinery_readings!: Table<LogbookMachineryReadings, string>;
  logbook_audit_trail!: Table<LogbookAuditTrail, string>;
  
  // EU-MRV tables
  mrv_monitoring_plans!: Table<MRVMonitoringPlan, string>;
  mrv_fuel_consumption!: Table<FuelConsumptionEntry, string>;
  mrv_annual_reports!: Table<MRVAnnualReport, string>;
  mrv_voyage_reports!: Table<MRVVoyageReport, string>;
  mrv_port_emissions!: Table<MRVPortEmissions, string>;
  mrv_thetis_submissions!: Table<THETISMRVSubmission, string>;
  
  // IHM (Inventory of Hazardous Materials) tables
  ihm_documents!: Table<IHMDocument, string>;
  hazardous_materials!: Table<HazardousMaterial, string>;
  safety_data_sheets!: Table<SafetyDataSheet, string>;
  material_samples!: Table<MaterialSample, string>;
  ihm_inspections!: Table<IHMInspection, string>;
  material_removals!: Table<MaterialRemoval, string>;

  constructor() {
    super('MARSXDatabase');

    // ============================================================================
    // DATABASE VERSION HISTORY
    // ============================================================================
    // Version 1: Clean start with vesselId standard
    // - Complete maritime management schema
    // - All vessel references use vesselId (NOT shipId)
    // - No migration from old versions (fresh deployment)
    //
    // Version 2: Added immutable audit trail table
    // - auditTrail table for complete change history
    // - Supports soft deletes and regulatory compliance
    // ============================================================================

    this.version(1).stores({
      ships: 'id, imoNumber, name, company, status',
      
      // Certificates
      certificates: 'id, certificateNumber, certificateType, expiryDate, status, issuingAuthority, vesselId, organizationId',
      
      // Maintenance (added organizationId index + equipmentName for search)
      equipment: 'id, equipmentCode, equipmentName, category, location, status, criticalityLevel, vesselId, organizationId',
      maintenanceRecords: 'id, equipmentId, performedDate, status, vesselId, organizationId',
      workOrders: 'id, equipmentId, status, priority, scheduledDate, requestedDate, vesselId, organizationId',
      
      // Crew (added organizationId index)
      personnel: 'id, employeeNumber, rank, department, status, vesselId, nationality, organizationId',
      crewCertifications: 'id, personnelId, certificateType, expiryDate, status',
      workingHours: 'id, personnelId, date, vesselId',
      trainings: 'id, trainingCode, category, required',
      trainingRecords: 'id, personnelId, trainingId, completionDate, expiryDate',
      
      // Safety
      inspections: 'id, inspectionType, frequency, scheduledDate, status, vesselId',
      incidents: 'id, incidentNumber, incidentType, severity, occurredDate, status, vesselId',
      drills: 'id, drillType, scheduledDate, status, vesselId',
      riskAssessments: 'id, assessmentDate, assessmentType, vesselId',
      
      // Inventory
      inventoryItems: 'id, partNumber, category, location, vesselId',
      stockTransactions: 'id, itemId, transactionType, transactionDate, vesselId',
      requisitions: 'id, requisitionNumber, department, requestedDate, status, vesselId',
      
      // Documents
      documents: 'id, filename, category, documentType, accessLevel, uploadedAt, vesselId',
      
      // Environmental
      oilRecords: 'id, date, operationType, vesselId',
      garbageRecords: 'id, date, garbageType, disposalMethod, vesselId',
      ballastRecords: 'id, date, operationType, vesselId',
      emissionRecords: 'id, date, fuelType, vesselId',
      
      // Navigation (Enhanced with maritime fields)
      positions: 'id, timestamp, vesselId, status, officer',
      passages: 'id, departurePort, arrivalPort, plannedDeparture, status, vesselId',
      waypoints: 'id, passageId, waypointNumber, latitude, longitude',
      portCalls: 'id, portName, plannedArrival, status, vesselId',
      
      // Compliance
      audits: 'id, auditNumber, auditType, scheduledDate, status, vesselId',
      nonConformities: 'id, ncNumber, severity, status, identifiedDate, vesselId',
      complianceChecks: 'id, checkType, checkDate, compliant, vesselId',
      
      // System
      users: 'id, username, email, role, status',
      notifications: 'id, userId, type, priority, read, createdAt',
      kpis: 'id, kpiName, category, period',
      performanceMetrics: 'id, metricName, category, date',
      syncQueue: 'id, entity, entityId, operation, status, timestamp',
      
      // Forms & Actions
      forms: 'id, organizationId, vesselId, formName, category, sortOrder',
      form_submissions: 'id, organizationId, vesselId, formTemplateId, status, submittedBy, submittedAt',
      corrective_actions: 'id, organizationId, vesselId, status, severity, assignedTo, reportedBy, reportedDate, dueDate',
      
      // Electronic Logbook
      logbook_entries: 'id, organizationId, vesselId, logbookType, entryNumber, entryDate, status, watchPeriodId, watchKeeperId, createdBy, createdAt',
      logbook_templates: 'id, organizationId, logbookType, shipType, isDefault, templateName',
      logbook_watch_periods: 'id, organizationId, vesselId, watchType, officerId, isActive, effectiveFrom, effectiveTo',
      logbook_machinery_readings: 'id, logbookEntryId, organizationId, vesselId, equipmentId, readingDatetime',
      logbook_audit_trail: 'id, logbookEntryId, action, changedBy, changedAt',
      
      // EU-MRV Emissions Monitoring
      mrv_monitoring_plans: 'id, vesselId, organizationId, status, validFrom, validUntil, planVersion',
      mrv_fuel_consumption: 'id, vesselId, organizationId, periodStart, periodEnd, periodType, voyageType, verificationStatus',
      mrv_annual_reports: 'id, vesselId, organizationId, reportingYear, complianceStatus, submittedToEU',
      mrv_voyage_reports: 'id, vesselId, organizationId, voyageNumber, departureDate, arrivalDate, voyageType, annualReportId',
      mrv_port_emissions: 'id, vesselId, organizationId, portName, arrivalDate, departureDate',
      mrv_thetis_submissions: 'id, annualReportId, organizationId, reportingYear, status, thetisReferenceNumber, submissionDate',
      
      // IHM (Inventory of Hazardous Materials)
      ihm_documents: 'id, vesselId, organizationId, imoNumber, status, certificationStatus, ihmVersion, ihmDate, nextSurveyDate',
      hazardous_materials: 'id, vesselId, organizationId, ihmDocumentId, materialName, category, classification, status, riskLevel, locationDeck, locationCompartment, nextInspectionDate, casNumber',
      safety_data_sheets: 'id, organizationId, vesselId, productName, manufacturer, casNumber, status, revisionDate, expiryDate',
      material_samples: 'id, vesselId, materialId, sampleNumber, sampleDate, status, laboratoryName',
      ihm_inspections: 'id, vesselId, ihmDocumentId, inspectionType, inspectionDate, status, overallResult, followUpRequired',
      material_removals: 'id, vesselId, materialId, removalDate, removedBy, disposalFacility, verifiedBy',
    }).upgrade(async () => {
      logger.info('📦 Database v1: Clean start with vesselId standard');
      logger.info('   All 70+ tables use vesselId indexes (industry standard)');
      logger.info('   Enhanced position schema with maritime fields (status, officer)');
      logger.info('✅ v1 initialization complete');
    });

    // Version 2: Add audit trail + organizationId indexes for better filtering
    this.version(2).stores({
      ships: 'id, imoNumber, name, company, status',
      
      // Certificates
      certificates: 'id, certificateNumber, certificateType, expiryDate, status, issuingAuthority, vesselId, organizationId',
      
      // Maintenance (added organizationId index + equipmentName for search)
      equipment: 'id, equipmentCode, equipmentName, category, location, status, criticalityLevel, vesselId, organizationId',
      maintenanceRecords: 'id, equipmentId, performedDate, status, vesselId, organizationId',
      workOrders: 'id, equipmentId, status, priority, scheduledDate, requestedDate, vesselId, organizationId',
      
      // Crew (added organizationId index)
      personnel: 'id, employeeNumber, rank, department, status, vesselId, nationality, organizationId',
      crewCertifications: 'id, personnelId, certificateType, expiryDate, status',
      workingHours: 'id, personnelId, date, vesselId',
      trainings: 'id, trainingCode, category, required',
      trainingRecords: 'id, personnelId, trainingId, completionDate, expiryDate',
      
      // Safety
      inspections: 'id, inspectionType, frequency, scheduledDate, status, vesselId',
      incidents: 'id, incidentNumber, incidentType, severity, occurredDate, status, vesselId',
      drills: 'id, drillType, scheduledDate, status, vesselId',
      riskAssessments: 'id, assessmentDate, assessmentType, vesselId',
      
      // Inventory
      inventoryItems: 'id, partNumber, category, location, vesselId',
      stockTransactions: 'id, itemId, transactionType, transactionDate, vesselId',
      requisitions: 'id, requisitionNumber, department, requestedDate, status, vesselId',
      
      // Documents
      documents: 'id, filename, category, documentType, accessLevel, uploadedAt, vesselId',
      
      // Environmental
      oilRecords: 'id, date, operationType, vesselId',
      garbageRecords: 'id, date, garbageType, disposalMethod, vesselId',
      ballastRecords: 'id, date, operationType, vesselId',
      emissionRecords: 'id, date, fuelType, vesselId',
      
      // Navigation (Enhanced with maritime fields)
      positions: 'id, timestamp, vesselId, status, officer',
      passages: 'id, departurePort, arrivalPort, plannedDeparture, status, vesselId',
      waypoints: 'id, passageId, waypointNumber, latitude, longitude',
      portCalls: 'id, portName, plannedArrival, status, vesselId',
      
      // Compliance
      audits: 'id, auditNumber, auditType, scheduledDate, status, vesselId',
      nonConformities: 'id, ncNumber, severity, status, identifiedDate, vesselId',
      complianceChecks: 'id, checkType, checkDate, compliant, vesselId',
      
      // System
      users: 'id, username, email, role, status',
      notifications: 'id, userId, type, priority, read, createdAt',
      kpis: 'id, kpiName, category, period',
      performanceMetrics: 'id, metricName, category, date',
      syncQueue: 'id, entity, entityId, operation, status, timestamp',
      
      // Forms & Actions
      forms: 'id, organizationId, vesselId, formName, category, sortOrder',
      form_submissions: 'id, organizationId, vesselId, formTemplateId, status, submittedBy, submittedAt',
      corrective_actions: 'id, organizationId, vesselId, status, severity, assignedTo, reportedBy, reportedDate, dueDate',
      
      // Audit Trail (Immutable, Append-Only) - Added in v2
      auditTrail: 'id, entityType, entityId, operation, userId, timestamp, [entityType+entityId]',
      
      // Electronic Logbook
      logbook_entries: 'id, organizationId, vesselId, logbookType, entryNumber, entryDate, status, watchPeriodId, watchKeeperId, createdBy, createdAt',
      logbook_templates: 'id, organizationId, logbookType, shipType, isDefault, templateName',
      logbook_watch_periods: 'id, organizationId, vesselId, watchType, officerId, isActive, effectiveFrom, effectiveTo',
      logbook_machinery_readings: 'id, logbookEntryId, organizationId, vesselId, equipmentId, readingDatetime',
      logbook_audit_trail: 'id, logbookEntryId, action, changedBy, changedAt',
      
      // EU-MRV Emissions Monitoring
      mrv_monitoring_plans: 'id, vesselId, organizationId, status, validFrom, validUntil, planVersion',
      mrv_fuel_consumption: 'id, vesselId, organizationId, periodStart, periodEnd, periodType, voyageType, verificationStatus',
      mrv_annual_reports: 'id, vesselId, organizationId, reportingYear, complianceStatus, submittedToEU',
      mrv_voyage_reports: 'id, vesselId, organizationId, voyageNumber, departureDate, arrivalDate, voyageType, annualReportId',
      mrv_port_emissions: 'id, vesselId, organizationId, portName, arrivalDate, departureDate',
      mrv_thetis_submissions: 'id, annualReportId, organizationId, reportingYear, status, thetisReferenceNumber, submissionDate',
      
      // IHM (Inventory of Hazardous Materials)
      ihm_documents: 'id, vesselId, organizationId, imoNumber, status, certificationStatus, ihmVersion, ihmDate, nextSurveyDate',
      hazardous_materials: 'id, vesselId, organizationId, ihmDocumentId, materialName, category, classification, status, riskLevel, locationDeck, locationCompartment, nextInspectionDate, casNumber',
      safety_data_sheets: 'id, organizationId, vesselId, productName, manufacturer, casNumber, status, revisionDate, expiryDate',
      material_samples: 'id, vesselId, materialId, sampleNumber, sampleDate, status, laboratoryName',
      ihm_inspections: 'id, vesselId, ihmDocumentId, inspectionType, inspectionDate, status, overallResult, followUpRequired',
      material_removals: 'id, vesselId, materialId, removalDate, removedBy, disposalFacility, verifiedBy',
    }).upgrade(async () => {
      logger.info('📦 Database v2: Added immutable audit trail table');
      logger.info('   auditTrail table for complete change history');
      logger.info('   Supports soft deletes and regulatory compliance');
      logger.info('✅ v2 upgrade complete');
    });

    this.version(1).stores({
      ships: 'id, imoNumber, name, company, status',
      
      // Certificates
      certificates: 'id, certificateNumber, certificateType, expiryDate, status, issuingAuthority, vesselId, organizationId',
      
      // Maintenance (added organizationId index + equipmentName for search)
      equipment: 'id, equipmentCode, equipmentName, category, location, status, criticalityLevel, vesselId, organizationId',
      maintenanceRecords: 'id, equipmentId, performedDate, status, vesselId, organizationId',
      workOrders: 'id, equipmentId, status, priority, scheduledDate, requestedDate, vesselId, organizationId',
      
      // Crew (added organizationId index)
      personnel: 'id, employeeNumber, rank, department, status, vesselId, nationality, organizationId',
      crewCertifications: 'id, personnelId, certificateType, expiryDate, status',
      workingHours: 'id, personnelId, date, vesselId',
      trainings: 'id, trainingCode, category, required',
      trainingRecords: 'id, personnelId, trainingId, completionDate, expiryDate',
      
      // Safety
      inspections: 'id, inspectionType, frequency, scheduledDate, status, vesselId',
      incidents: 'id, incidentNumber, incidentType, severity, occurredDate, status, vesselId',
      drills: 'id, drillType, scheduledDate, status, vesselId',
      riskAssessments: 'id, assessmentDate, assessmentType, vesselId',
      
      // Inventory
      inventoryItems: 'id, partNumber, category, location, vesselId',
      stockTransactions: 'id, itemId, transactionType, transactionDate, vesselId',
      requisitions: 'id, requisitionNumber, department, requestedDate, status, vesselId',
      
      // Documents
      documents: 'id, filename, category, documentType, accessLevel, uploadedAt, vesselId',
      
      // Environmental
      oilRecords: 'id, date, operationType, vesselId',
      garbageRecords: 'id, date, garbageType, disposalMethod, vesselId',
      ballastRecords: 'id, date, operationType, vesselId',
      emissionRecords: 'id, date, fuelType, vesselId',
      
      // Navigation (Enhanced with maritime fields)
      positions: 'id, timestamp, vesselId, status, officer',
      passages: 'id, departurePort, arrivalPort, plannedDeparture, status, vesselId',
      waypoints: 'id, passageId, waypointNumber, latitude, longitude',
      portCalls: 'id, portName, plannedArrival, status, vesselId',
      
      // Compliance
      audits: 'id, auditNumber, auditType, scheduledDate, status, vesselId',
      nonConformities: 'id, ncNumber, severity, status, identifiedDate, vesselId',
      complianceChecks: 'id, checkType, checkDate, compliant, vesselId',
      
      // System
      users: 'id, username, email, role, status',
      notifications: 'id, userId, type, priority, read, createdAt',
      kpis: 'id, kpiName, category, period',
      performanceMetrics: 'id, metricName, category, date',
      syncQueue: 'id, entity, entityId, operation, status, timestamp',
      
      // Forms & Actions
      forms: 'id, organizationId, vesselId, formName, category, sortOrder',
      form_submissions: 'id, organizationId, vesselId, formTemplateId, status, submittedBy, submittedAt',
      corrective_actions: 'id, organizationId, vesselId, status, severity, assignedTo, reportedBy, reportedDate, dueDate',
      
      // Audit Trail (Immutable, Append-Only)
      auditTrail: 'id, entityType, entityId, operation, userId, timestamp, [entityType+entityId]',
      
      // Electronic Logbook
      logbook_entries: 'id, organizationId, vesselId, logbookType, entryNumber, entryDate, status, watchPeriodId, watchKeeperId, createdBy, createdAt',
      logbook_templates: 'id, organizationId, logbookType, shipType, isDefault, templateName',
      logbook_watch_periods: 'id, organizationId, vesselId, watchType, officerId, isActive, effectiveFrom, effectiveTo',
      logbook_machinery_readings: 'id, logbookEntryId, organizationId, vesselId, equipmentId, readingDatetime',
      logbook_audit_trail: 'id, logbookEntryId, action, changedBy, changedAt',
      
      // EU-MRV Emissions Monitoring
      mrv_monitoring_plans: 'id, vesselId, organizationId, status, validFrom, validUntil, planVersion',
      mrv_fuel_consumption: 'id, vesselId, organizationId, periodStart, periodEnd, periodType, voyageType, verificationStatus',
      mrv_annual_reports: 'id, vesselId, organizationId, reportingYear, complianceStatus, submittedToEU',
      mrv_voyage_reports: 'id, vesselId, organizationId, voyageNumber, departureDate, arrivalDate, voyageType, annualReportId',
      mrv_port_emissions: 'id, vesselId, organizationId, portName, arrivalDate, departureDate',
      mrv_thetis_submissions: 'id, annualReportId, organizationId, reportingYear, status, thetisReferenceNumber, submissionDate',
      
      // IHM (Inventory of Hazardous Materials)
      ihm_documents: 'id, vesselId, organizationId, imoNumber, status, certificationStatus, ihmVersion, ihmDate, nextSurveyDate',
      hazardous_materials: 'id, vesselId, organizationId, ihmDocumentId, materialName, category, classification, status, riskLevel, locationDeck, locationCompartment, nextInspectionDate, casNumber',
      safety_data_sheets: 'id, organizationId, vesselId, productName, manufacturer, casNumber, status, revisionDate, expiryDate',
      material_samples: 'id, vesselId, materialId, sampleNumber, sampleDate, status, laboratoryName',
      ihm_inspections: 'id, vesselId, ihmDocumentId, inspectionType, inspectionDate, status, overallResult, followUpRequired',
      material_removals: 'id, vesselId, materialId, removalDate, removedBy, disposalFacility, verifiedBy',
    }).upgrade(async () => {
      logger.info('📦 Database v1: Clean start with vesselId standard');
      logger.info('   All 70+ tables use vesselId indexes (industry standard)');
      logger.info('   Enhanced position schema with maritime fields (status, officer)');
      logger.info('✅ v1 initialization complete');
    });

    // ============================================================================
    // SCHEMA VALIDATION HOOKS
    // ============================================================================
    // Enforce schema validation for critical tables to prevent invalid data
    this.setupValidationHooks();
  }

  /**
   * Setup validation hooks for schema enforcement
   */
  private setupValidationHooks(): void {
    // Certificate validation
    this.certificates.hook('creating', (_primKey, obj) => {
      // Auto-set certificateName if missing
      if (!(obj as any).certificateName && (obj as any).certificateType) {
        (obj as any).certificateName = (obj as any).certificateType.charAt(0).toUpperCase() + (obj as any).certificateType.slice(1) + ' Certificate';
      }
      // Auto-set timestamps if missing
      const now = new Date();
      if (!(obj as any).createdAt) (obj as any).createdAt = now;
      if (!(obj as any).updatedAt) (obj as any).updatedAt = now;
      this.validateCertificate(obj);
    });

    this.certificates.hook('updating', (modifications, _primKey, obj) => {
      // Auto-update updatedAt
      (modifications as any).updatedAt = new Date();
      // Merge modifications with existing object for validation
      const updated = { ...(obj as any), ...(modifications as any) };
      // Auto-set certificateName if missing (can happen with put operations)
      if (!updated.certificateName && updated.certificateType) {
        updated.certificateName = updated.certificateType.charAt(0).toUpperCase() + updated.certificateType.slice(1) + ' Certificate';
        (modifications as any).certificateName = updated.certificateName;
      }
      this.validateCertificate(updated);
    });

    logger.info('✅ Schema validation hooks installed');
  }

  /**
   * Validate certificate data
   */
  private validateCertificate(cert: any): void {
    const errors: string[] = [];

    // Required fields
    // certificateName should be set by hook if missing, but validate it exists
    if (!cert.certificateName || (typeof cert.certificateName === 'string' && cert.certificateName.trim() === '')) {
      errors.push('Certificate name is required');
    }
    if (!cert.certificateNumber || (typeof cert.certificateNumber === 'string' && cert.certificateNumber.trim() === '')) {
      errors.push('Certificate number is required');
    }
    if (!cert.certificateType || (typeof cert.certificateType === 'string' && cert.certificateType.trim() === '')) {
      errors.push('Certificate type is required');
    }
    if (!cert.issueDate) {
      errors.push('Issue date is required');
    }
    if (!cert.expiryDate) {
      errors.push('Expiry date is required');
    }
    if (!cert.issuingAuthority || (typeof cert.issuingAuthority === 'string' && cert.issuingAuthority.trim() === '')) {
      errors.push('Issuing authority is required');
    }

    // Date validation
    if (cert.issueDate && cert.expiryDate) {
      const issue = new Date(cert.issueDate);
      const expiry = new Date(cert.expiryDate);
      
      if (isNaN(issue.getTime())) {
        errors.push('Issue date must be a valid date');
      }
      if (isNaN(expiry.getTime())) {
        errors.push('Expiry date must be a valid date');
      }
      if (!isNaN(issue.getTime()) && !isNaN(expiry.getTime()) && expiry <= issue) {
        errors.push('Expiry date must be after issue date');
      }
    }

    // Certificate number format (basic validation)
    if (cert.certificateNumber && typeof cert.certificateNumber === 'string' && cert.certificateNumber.length < 3) {
      errors.push('Certificate number must be at least 3 characters');
    }

    if (errors.length > 0) {
      logger.error('Certificate validation failed', { errors, cert });
      throw new Error(`Certificate validation failed: ${errors.join(', ')}`);
    }
  }

  /**
   * Initialize database with sample data (for development)
   */
  async initializeSampleData(): Promise<void> {
    logger.info('Initializing sample data');
    
    // Check if data already exists
    const shipCount = await this.ships.count();
    if (shipCount > 0) {
      logger.info('Database already has data, skipping initialization');
      return;
    }

    try {
      // Add sample ships (using put instead of bulkAdd to avoid duplicate key errors)
      await this.ships.bulkPut([
      {
        id: '4f00a90c-5c69-503f-a146-96f0e51354c1',
        name: 'MUSTERSHIP',
        company: 'MARCARE',
        imoNumber: '9212345',
        flag: 'Unknown',
        vesselType: 'General Cargo',
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: 'afc793ee-d6f9-54d4-83b3-59e8f01d786b',
        name: 'Ship 2 (Antonia B)',
        company: 'MARCARE',
        imoNumber: '8318128',
        flag: 'Unknown',
        vesselType: 'General Cargo',
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ]);

      logger.info('Added 2 REAL ships from legacy MARS2 system to Dexie');

      // Add sample certificates for ship-001
      await this.certificates.bulkPut([
      {
        id: 'cert-001',
        certificateNumber: 'CERT-001',
        certificateName: 'Safety Management Certificate',
        certificateType: 'ISM',
        issuingAuthority: 'Flag State',
        issueDate: new Date('2024-01-01'),
        expiryDate: new Date('2025-12-31'),
        status: 'valid',
        issuedBy: 'Maritime Authority',
        vesselId: '4f00a90c-5c69-503f-a146-96f0e51354c1',
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: 'cert-002',
        certificateNumber: 'CERT-002',
        certificateName: 'Load Line Certificate',
        certificateType: 'Statutory',
        issuingAuthority: 'Classification Society',
        issueDate: new Date('2023-06-01'),
        expiryDate: new Date('2025-06-01'),
        status: 'valid',
        issuedBy: 'DNV GL',
        vesselId: '4f00a90c-5c69-503f-a146-96f0e51354c1',
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: 'cert-003',
        certificateNumber: 'CERT-003',
        certificateName: 'IOPP Certificate',
        certificateType: 'MARPOL',
        issuingAuthority: 'Flag State',
        issueDate: new Date('2023-01-01'),
        expiryDate: new Date('2025-02-15'),
        status: 'expiring_soon',
        issuedBy: 'Maritime Authority',
        vesselId: '4f00a90c-5c69-503f-a146-96f0e51354c1',
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ]);

      logger.info('Sample data initialized');
    } catch (error) {
      // Silently handle duplicate key errors (happens in React StrictMode)
      if (error instanceof Error && error.name === 'BulkError') {
        logger.info('Sample data already exists (duplicate key), skipping initialization');
      } else {
        logger.error('Error initializing sample data', error);
        throw error;
      }
    }
  }

  /**
   * Clear all data (for testing)
   */
  async clearAllData(): Promise<void> {
    await Promise.all([
      this.certificates.clear(),
      this.equipment.clear(),
      this.maintenanceRecords.clear(),
      this.workOrders.clear(),
      this.personnel.clear(),
      this.crewCertifications.clear(),
      this.workingHours.clear(),
      this.trainings.clear(),
      this.trainingRecords.clear(),
      this.inspections.clear(),
      this.incidents.clear(),
      this.drills.clear(),
      this.riskAssessments.clear(),
      this.inventoryItems.clear(),
      this.stockTransactions.clear(),
      this.requisitions.clear(),
      this.documents.clear(),
      this.oilRecords.clear(),
      this.garbageRecords.clear(),
      this.ballastRecords.clear(),
      this.emissionRecords.clear(),
      this.positions.clear(),
      this.passages.clear(),
      this.waypoints.clear(),
      this.portCalls.clear(),
      this.audits.clear(),
      this.nonConformities.clear(),
      this.complianceChecks.clear(),
      this.users.clear(),
      this.notifications.clear(),
      this.kpis.clear(),
      this.performanceMetrics.clear(),
      this.syncQueue.clear(),
      this.forms.clear(),
      this.form_submissions.clear(),
      this.corrective_actions.clear(),
      this.logbook_entries.clear(),
      this.logbook_templates.clear(),
      this.logbook_watch_periods.clear(),
      this.logbook_machinery_readings.clear(),
      this.logbook_audit_trail.clear(),
    ]);
    
    logger.info('All data cleared');
  }

  /**
   * Get database statistics
   */
  async getStatistics() {
    const stats = {
      ships: await this.ships.count(),
      certificates: await this.certificates.count(),
      equipment: await this.equipment.count(),
      maintenanceRecords: await this.maintenanceRecords.count(),
      workOrders: await this.workOrders.count(),
      personnel: await this.personnel.count(),
      crewCertifications: await this.crewCertifications.count(),
      inspections: await this.inspections.count(),
      incidents: await this.incidents.count(),
      drills: await this.drills.count(),
      inventoryItems: await this.inventoryItems.count(),
      requisitions: await this.requisitions.count(),
      documents: await this.documents.count(),
      oilRecords: await this.oilRecords.count(),
      garbageRecords: await this.garbageRecords.count(),
      ballastRecords: await this.ballastRecords.count(),
      positions: await this.positions.count(),
      passages: await this.passages.count(),
      portCalls: await this.portCalls.count(),
      audits: await this.audits.count(),
      nonConformities: await this.nonConformities.count(),
      users: await this.users.count(),
      notifications: await this.notifications.count(),
      syncQueue: await this.syncQueue.count(),
      logbookEntries: await this.logbook_entries.count(),
      logbookTemplates: await this.logbook_templates.count(),
      logbookWatchPeriods: await this.logbook_watch_periods.count(),
      logbookMachineryReadings: await this.logbook_machinery_readings.count(),
      logbookAuditTrail: await this.logbook_audit_trail.count(),
      auditTrail: await this.auditTrail.count(), // Immutable audit trail
      total: 0,
    };

    stats.total = Object.values(stats).reduce((sum, count) => sum + count, 0) - stats.total;
    
    return stats;
  }

  /**
   * Audit database state - diagnose data issues
   */
  async auditDatabase(): Promise<any> {
    logger.info('Starting database audit');
    
    try {
      const ships = await this.ships.toArray();
      const certificates = await this.certificates.toArray();
      const personnel = await this.personnel.toArray();
      const equipment = await this.equipment.toArray();
      const documents = await this.documents.toArray();
      
      logger.info('Database Audit Results:', {
        ships: { count: ships.length, details: ships.length > 0 ? ships.map((s: any) => `${s.name} (${s.id})`) : 'EMPTY' },
        certificates: { count: certificates.length, details: certificates.length > 0 ? `with vesselIds: ${new Set(certificates.map((c: any) => c.vesselId)).size} unique` : 'EMPTY' },
        personnel: personnel.length,
        equipment: equipment.length,
        documents: documents.length
      });
      
      // Check certificate-vessel relationships
      if (certificates.length > 0) {
        const certsByVessel = new Map();
        certificates.forEach((cert: any) => {
          if (!certsByVessel.has(cert.vesselId)) {
            certsByVessel.set(cert.vesselId, []);
          }
          certsByVessel.get(cert.vesselId).push(cert.certificateNumber);
        });
        
        const relationships = Array.from(certsByVessel.entries()).map(([vesselId, certs]: [string, any[]]) => {
          const ship = ships.find((s: any) => s.id === vesselId);
          return `${ship?.name || vesselId}: ${certs.length} certificates`;
        });
        
        logger.info('Certificate-Vessel Relationships', { relationships });
      }
      
      return {
        ships: ships.length,
        certificates: certificates.length,
        personnel: personnel.length,
        equipment: equipment.length,
        documents: documents.length,
        hasData: ships.length > 0,
      };
    } catch (error) {
      logger.error('Audit error', error);
      throw error;
    }
  }

  /**
   * Export database to JSON
   */
  async exportToJSON(): Promise<string> {
    const data = {
      certificates: await this.certificates.toArray(),
      equipment: await this.equipment.toArray(),
      maintenanceRecords: await this.maintenanceRecords.toArray(),
      workOrders: await this.workOrders.toArray(),
      personnel: await this.personnel.toArray(),
      // ... add other tables as needed
    };

    return JSON.stringify(data, null, 2);
  }

  /**
   * Import database from JSON
   */
  async importFromJSON(jsonData: string): Promise<void> {
    const data = JSON.parse(jsonData);

    await this.transaction('rw', this.tables, async () => {
      if (data.certificates) await this.certificates.bulkAdd(data.certificates);
      if (data.equipment) await this.equipment.bulkAdd(data.equipment);
      if (data.maintenanceRecords) await this.maintenanceRecords.bulkAdd(data.maintenanceRecords);
      if (data.workOrders) await this.workOrders.bulkAdd(data.workOrders);
      if (data.personnel) await this.personnel.bulkAdd(data.personnel);
      // ... add other tables as needed
    });

    logger.info('Data imported successfully');
  }
}

// Create and export database instance
export const db = new MARSXDatabase();

// Initialize sample data on first run
if (typeof window !== 'undefined') {
  db.open().then(() => {
    logger.info('Database opened successfully');
    // Uncomment to initialize sample data on first run:
    // db.initializeSampleData();
  }).catch(error => {
    logger.error('Failed to open database', error);
  });
}

